import * as RadioGroup from '@givebutter/primitives/radio-group';
import { faCalendar, faShoppingCart } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as CampaignItemForm from 'assets/js/pages/campaign-settings/campaign-event/CampaignItemForm';
import { TicketCustomFieldFormValues } from 'assets/js/pages/campaign-settings/campaign-event/types';
import * as Form from 'components/form';
import { twJoin } from 'tailwind-merge';
import { CampaignEvent } from 'types/domain';

interface TicketFormData extends CampaignItemForm.BaseType {
    subtype: 'physical' | 'digital';
    bundleOnly: boolean;
    custom_fields: TicketCustomFieldFormValues[];
    bundle_unlimited: boolean;
    bundles: CampaignItemForm.ItemFormType[];
}

interface TicketFormHeaderProps {
    eventType: CampaignEvent['type'];
}

const TicketFormHeader = (props: TicketFormHeaderProps) => {
    const { eventType } = props;
    const { control, path } = Form.useGivebutterFormContext<TicketFormData>();

    return (
        <div className={'flex flex-col gap-y-6'}>
            <div className={'flex flex-col gap-6 gap-x-4'}>
                <section
                    className={twJoin(
                        'flex flex-col gap-y-2',
                        eventType === 'hybrid' ? 'col-span-1' : 'col-span-full',
                    )}
                >
                    <Form.Label name={'name'}>
                        Ticket name
                        <Form.LabelAsterisk />
                    </Form.Label>
                    <Form.TextInputRoot
                        name={'name'}
                        control={control}
                        rules={{ required: 'The name is required.' }}
                    >
                        <Form.TextInputForm placeholder={'General Admission'} />
                    </Form.TextInputRoot>
                    <Form.UseFormError name={'name'} />
                </section>
                {eventType === 'hybrid' && (
                    <section className={'col-span-1 flex flex-col gap-2'}>
                        <Form.Label name={path('subtype')}>Ticket type</Form.Label>
                        <Form.UseFormRadioGroup
                            name={'subtype'}
                            control={control}
                            rules={{ required: 'The ticket type is required.' }}
                            variant={'card'}
                        >
                            <RadioGroup.Item className={'flex gap-x-3'} value={'physical'}>
                                <div
                                    className={`flex aspect-square h-10 items-center justify-center rounded-full bg-gray-100`}
                                >
                                    <FontAwesomeIcon icon={faCalendar} className="text-gray-600" />
                                </div>
                                <div className={'flex flex-1 flex-col gap-1'}>
                                    <Form.Label
                                        name={path('subtype')}
                                        htmlFor={'physical'}
                                        className={'cursor-pointer font-medium'}
                                    >
                                        In-person
                                    </Form.Label>
                                    <div className="text-sm text-gray-500">
                                        Generates a PDF with a QR code for check-in.
                                    </div>
                                </div>
                                <RadioGroup.IndicatorContainer>
                                    <RadioGroup.Indicator />
                                </RadioGroup.IndicatorContainer>
                            </RadioGroup.Item>
                            <RadioGroup.Item className={'flex gap-x-3'} value={'digital'}>
                                <div
                                    className={`flex aspect-square h-10 items-center justify-center rounded-full bg-gray-100`}
                                >
                                    <FontAwesomeIcon
                                        icon={faShoppingCart}
                                        className="text-gray-600"
                                    />
                                </div>
                                <div className={'flex flex-1 flex-col'}>
                                    <Form.Label
                                        name={path('subtype')}
                                        htmlFor={'digital'}
                                        className={'cursor-pointer font-medium'}
                                    >
                                        Digital
                                    </Form.Label>
                                    <div className={'text-sm text-gray-600'}>
                                        For digital events that don't require a physical ticket.
                                    </div>
                                </div>
                                <RadioGroup.IndicatorContainer>
                                    <RadioGroup.Indicator />
                                </RadioGroup.IndicatorContainer>
                            </RadioGroup.Item>
                        </Form.UseFormRadioGroup>
                        <Form.UseFormError name={'subtype'} />
                    </section>
                )}
            </div>
        </div>
    );
};

export { TicketFormHeader };
